import { useCallback, useEffect, useState } from "react";
import Button from "components/global/Button";
import FormWrapper from "components/global/FormWrapper";
import { showAlert } from "utils/utils";
import Spacer from "components/global/Spacer";
import styled from "styled-components";
import { connect } from "react-redux";
import {
  createBrand,
  getBrands,
  getTooltip,
  getUserAccounts,
  setLoggedIn,
  trackEvents,
  updateTooltip,
} from "api";
import Loader from "components/global/Loader";
import { useHistory, useLocation, Redirect } from "react-router-dom";
import RadioInput from "components/global/RadioInput";
import FormGroupCustom from "components/global/FormGroupCustom";
import CreateBrandWithSocials from "./CreateBrandWithSocials";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .headerContainer {
    max-width: 400px;
    text-align: center;
    gap: 6px;

    p {
      font-size: 14px;
      line-height: 20px;
    }
  }

  .bodyContainer {
    margin: 0 auto;
    width: 455px;
    display: flex;
    flex-direction: column;
  }

  .inputContainer {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-self: center;
  }

  .upload-info {
    font-size: 12px;
    font-style: normal;
    font-weight: 450;
    margin-top: 8px;
    color: var(--body-text);
  }

  label {
    color: var(--body-text);
  }

  .logo-label {
    margin-bottom: 8px;
  }

  .submit-btn {
    width: 100%;
  }

  .banner {
    padding: 6px 4px;
    gap: 8px;
    border-radius: 8px;
    background-color: #e3effc;
    height: 48px;

    .p {
      color: #1d31a5;
    }
  }

  .footer {
    position: sticky;
    bottom: 0;
    background-color: #fff;
  }

  @media screen and (max-width: 768px) {
    margin-bottom: 100px;
    .headerContainer,
    .bodyContainer,
    .inputContainer {
      max-width: 327px;
    }
  }
`;

const CreateBrand = ({
  active_account,
  current_sub,
  brands,
  onboarding_state,
  user,
  user_token,
  refresh_token,
}) => {
  const history = useHistory();
  const { search } = useLocation();
  const postpaddyMobile = new URLSearchParams(search).get("postpaddy-mobile");
  const [userDetails, setUserDetails] = useState({
    name: user?.full_name ?? "",
    user_job: "",
    brand_link: "",
    brand_description: "",
    timezone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileUrl, setMobileURL] = useState(null);
  const [authMobileProvider, setAuthMobileProvider] = useState(null);

  const trackCreateBrand = useCallback(async () => {
    const data = {
      event_name: "created_brand",
      event_properties: {
        date: new Date(),
        account_type: current_sub?.plan_name,
        user_type: active_account?.industry,
      },
    };

    try {
      await trackEvents(data);
    } catch (error) {
      console.log(error);
    }
  }, [active_account?.industry, current_sub?.plan_name]);

  const submitForm = useCallback(async () => {
    setIsSubmitting(true);

    try {
      let values = { ...userDetails };
      if (!values.brand_link) {
        delete values?.brand_link;
      }

      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      values.name = values.name.trim();
      values.timezone = deviceTimezone;

      const res = await createBrand(values);
      if (res?.data?.status === true) {
        await getUserAccounts();
        await updateTooltip({
          ...onboarding_state,
          confirm_brand_details: true,
        });
        await getTooltip();
        await getBrands();
        await trackCreateBrand();

        if (postpaddyMobile || mobileUrl || authMobileProvider) {
          setLoggedIn(false);
          localStorage.removeItem("mobileUrl");
          localStorage.removeItem("authMobileProvider");
          window.location.href = `com.postpaddy.paddy://(brands)/screen-two?token=${user_token}&refresh_token=${refresh_token}&success=true`;
          return;
        }

        history.replace("/onboarding/connect-socials");
        setIsSubmitting(false);
      } else if (res?.data?.status === false) {
        setIsSubmitting(false);
        showAlert({
          type: "danger",
          msg: res.data.message,
        });
      } else {
        setIsSubmitting(false);
        showAlert({
          type: "danger",
          msg: "Something went wrong. Try refreshing the page or contact support",
        });
      }
    } catch (e) {
      setIsSubmitting(false);
      showAlert({
        type: "danger",
        msg: e.response?.data?.message ?? "An error occurred. Please contact support",
      });
    }
  }, [
    authMobileProvider,
    history,
    mobileUrl,
    onboarding_state,
    postpaddyMobile,
    refresh_token,
    trackCreateBrand,
    userDetails,
    user_token,
  ]);

  useEffect(() => {
    setMobileURL(localStorage.getItem("mobileUrl"));
    setAuthMobileProvider(localStorage.getItem("authMobileProvider"));
  }, []);

  if (!onboarding_state) {
    return <Loader />;
  }

  if (
    brands?.length &&
    onboarding_state?.confirm_brand_details &&
    !onboarding_state?.onboarding_completed
  ) {
    return <Redirect to={`/onboarding/connect-socials`} />;
  }

  if (
    !isSubmitting &&
    brands?.length &&
    !onboarding_state?.confirm_brand_details
  ) {
    return <CreateBrandWithSocials />;
  }

  return (
    <Wrapper>
      <div className="headerContainer">
        <h4>Tell us about your business</h4>
        <p className="l3 colorBodyText">
          We&apos;d love to learn more about your business so we can tailor our
          platform to suit your needs.
        </p>
      </div>
      <Spacer y={3} />
      <div className="inputContainer">
        <div className="brandForm">
          <>
            <FormWrapper>
              <FormGroupCustom
                fieldStyle="shortText"
                label="What is your business name"
                name="name"
                placeholder="e.g Creaveo creative"
                required={false}
                className="spanFull"
                value={userDetails.name}
                onChange={(e) =>
                  setUserDetails((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
              <label htmlFor="" className="spanFull">
                What best describes you?
              </label>
              <RadioInput
                key="businessOwner"
                name="businessOwner"
                id="businessOwner"
                checked={userDetails.user_job === "businessOwner"}
                onClick={() =>
                  setUserDetails((prev) => ({
                    ...prev,
                    user_job: "businessOwner",
                  }))
                }
                label="I am a business owner"
              />

              <RadioInput
                key="socialMediaManager"
                name="socialMediaManager"
                id="socialMediaManager"
                checked={userDetails.user_job === "socialMediaManager"}
                onClick={() =>
                  setUserDetails((prev) => ({
                    ...prev,
                    user_job: "socialMediaManager",
                  }))
                }
                label="I am a social media manager"
              />

              <FormGroupCustom
                fieldStyle="longText"
                label="Tell us about your business"
                name="brand_description"
                placeholder=""
                required={false}
                className="spanFull"
                onChange={(e) =>
                  setUserDetails((prev) => ({
                    ...prev,
                    brand_description: e.target.value,
                  }))
                }
                showTextCount
                maxLength={200}
              />

              <FormGroupCustom
                fieldStyle="shortText"
                label="Website URL (optional)"
                name="brand_link"
                placeholder="e.g https://www.mywebsite.com"
                required={false}
                className="spanFull"
                onChange={(e) =>
                  setUserDetails((prev) => ({
                    ...prev,
                    brand_link: e.target.value,
                  }))
                }
              />

              <p className="spanFull">Having problems? Click <span className="colorSecondary cursorPointer contactSupport">here</span> to contact support.</p>
            </FormWrapper>
            <div className="footer">
              <Button
                className="submit-btn"
                onClick={submitForm}
                text={"Done"}
                bg="var(--secondary)"
                loading={isSubmitting}
                disabled={
                  !userDetails.name ||
                  !userDetails.user_job ||
                  !userDetails.brand_description
                }
                fullWidth
              />
            </div>
          </>
        </div>
      </div>
    </Wrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    active_account: state.active_account,
    current_sub: state.current_sub,
    brands: state.brands,
    onboarding_state: state.guided_tours,
    user: state.user,
    baseUrl: state.ai_base_url,
    user_token: state.user_token,
    refresh_token: state.refresh_token,
  };
};

export default connect(mapStateToProps)(CreateBrand);
